import React, {
  ForwardRefRenderFunction,
  KeyboardEvent,
  MouseEvent,
  useRef,
  useState
} from "react";

import { CustomAvatar } from "../../AvatarSelectionUI";
import { AvatarType } from "../../AvatarType";
import ProfileIcon from "../../icons/Profile.svg";
import AvatarIcon from "../../icons/Avatar.svg";
import WearableIcon from "../../icons/Wearable.svg";
import ParcelIcon from "../../icons/Parcel.svg";
import LogoutIcon from "../../icons/Logout.svg";
import RefreshIcon from "../../icons/Refresh.svg";
import GuideIcon from "../../icons/Guide.svg";

import styles from "./AvatarSelectionUIComponent.module.css";

type AvatarSelectionUIProps = {
  parcelData: string;
  onUpdateUserAvatar: (avatar: AvatarType) => void;
  onReadUserParcel: (parcelNum: number) => void;
  onUpdateUserParcel: (parcelNum: number, mmlText: string) => void;
  onRefreshUserParcel: (parcelNum: number) => void;
  onSaveUserParcel: (parcelNum: number, mmlText: string) => void;
  onSaveUserName: (nameIns: string) => void;
  onGetUserNames: (refresh: boolean) => [];
  onSaveUserAvatar: (avatarIns: string) => void;
  onGetUserAvatars: (refresh: boolean) => [];
  onSaveUserWearable: (wearableIns: string) => void;
  onGetUserWearables: (refresh: boolean) => [];
  onGetUserParcels: (refresh: boolean) => [];
  visibleByDefault?: boolean;
  availableAvatars: AvatarType[];
  allowCustomAvatars?: boolean;
};

enum CustomAvatarType {
  meshFileUrl,
  mmlUrl,
  mml,
}

function SelectedPill() {
  return <span className={styles.selectedPill}>Selected</span>;
}

export const AvatarSelectionUIComponent: ForwardRefRenderFunction<any, AvatarSelectionUIProps> = (
  props: AvatarSelectionUIProps,
) => {
  const visibleByDefault: boolean = props.visibleByDefault ?? false;
  const [menuVisible, setMenuVisible] = useState<boolean>(visibleByDefault);
  const [selectedAvatar, setSelectedAvatar] = useState<CustomAvatar | undefined>(undefined);
  const [customAvatarType, setCustomAvatarType] = useState<CustomAvatarType>(
    CustomAvatarType.mmlUrl,
  );
  const [customAvatarValue, setCustomAvatarValue] = useState<string>("");
  const [userNameValue, setUserNameValue] = useState<string>(props.userData.username);
  const [userAvatarValue, setUserAvatarValue] = useState<string>(props.userData.avatar_ins);
  const [userWearablesValue, setUserWearablesValue] = useState<array>(props.userData.wearables_ins);
  const [parcelNumValue, setParcelNumValue] = useState<string>("");
  const [mmlTextValue, setMMLTextValue] = useState<string>(props.parcelData);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const parcelmmlRef = useRef<HTMLTextAreaElement>(null);
  const parcelnumRef = useRef<HTMLTextAreaElement>(null);

  const [confirmVisible, setConfirmVisible] = useState<boolean>(false);
  const [modified, setModified] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [nameVisible, setNameVisible] = useState<boolean>(false);
  const [avatarVisible, setAvatarVisible] = useState<boolean>(false);
  const [wearableVisible, setWearableVisible] = useState<boolean>(false);
  const [parcelVisible, setParcelVisible] = useState<boolean>(false);
  const [guideVisible, setGuideVisible] = useState<boolean>(false);
  const [namesInventory, setNamesInventory] = useState<array>([]);
  const [avatarsInventory, setAvatarsInventory] = useState<array>([]);
  const [wearablesInventory, setWearablesInventory] = useState<array>([]);
  const [parcelsInventory, setParcelsInventory] = useState<array>([]);

  // Computed property (implemented as a function)
  const parcelValue = () => {
    return parcelNumValue ? window.BITMAP_DATA.txns[parcelNumValue].size * window.BITMAP_DATA.columnSize - window.BITMAP_DATA.streetSize : null
  };

  const handleRootClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const selectAvatar = (avatar: CustomAvatar) => {
    setSelectedAvatar(avatar);
    props.onUpdateUserAvatar(avatar);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCustomAvatarValue(e.target.value);
  };
  const handleParcelNumChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setParcelNumValue(e.target.value);
    window.BITMAP_EDITING = e.target.value
    // Send request for parcel's MML contents
    props.onReadUserParcel(parseInt(e.target.value));
  };
  const handleParcelMMLChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setMMLTextValue(e.target.value);
    props.parcelData = e.target.value
    // Auto resize
    e.target.style.height = '0px';
    e.target.style.height = `${e.target.scrollHeight+5}px`;
    // Update parcel
    if(parcelNumValue)
      props.onUpdateUserParcel(parseInt(parcelNumValue), e.target.value);
    // Flag as recently modified
    setModified(true)
  };

  const handleParcelMMLInput = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.stopPropagation();
    if (e.key == 'Tab') {
      e.preventDefault();
      var start = e.target.selectionStart;
      var end = e.target.selectionEnd;

      // set textarea value to: text before caret + tab + text after caret
      e.target.value = e.target.value.substring(0, start) + "\t" + e.target.value.substring(end);

      // put caret at right position again
      e.target.selectionStart = e.target.selectionEnd = start + 1;
    }
    e.target.style.height = '0px';
    e.target.style.height = `${e.target.scrollHeight+5}px`;
  };

  const handleParcelMMLFocus = (e) => {
    // Auto resize
    e.target.style.height = '0px';
    e.target.style.height = `${e.target.scrollHeight+5}px`;
  };

  const handleParcelSave = (e: React.ClickEvent<HTMLButtonElement>) => {
    props.onSaveUserParcel(parseInt(parcelNumValue), props.parcelData);
    setParcelVisible(false)
    setConfirmVisible(false)
  };

  const handleTeleportClick = (parcelNum: number) => {
    window.open(window.location.origin+'/parcel/'+parcelNum,'_self')
  };

  const addCustomAvatar = () => {
    if (!customAvatarValue) {
      return;
    }

    const newSelectedAvatar = {
      mmlCharacterString: customAvatarType === CustomAvatarType.mml ? customAvatarValue : undefined,
      mmlCharacterUrl: customAvatarType === CustomAvatarType.mmlUrl ? customAvatarValue : undefined,
      meshFileUrl:
        customAvatarType === CustomAvatarType.meshFileUrl ? customAvatarValue : undefined,
      isCustomAvatar: true,
    } as CustomAvatar;

    setSelectedAvatar(newSelectedAvatar);
    props.onUpdateUserAvatar(newSelectedAvatar);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.stopPropagation();
  };

  const handleOpen = (e) => {
    handleClose()
    //console.log('VM',window.vm.$children[0])
    if(window.vm.$children[0].$children[1])
      window.vm.$children[0].$children[1].showModal = true
    else
      setMenuVisible(true)
  }
  const handleClose = (e) => {
    if(guideVisible) {
      setGuideVisible(false)
    }
    else if(nameVisible) {
      setNameVisible(false)
    }
    else if(avatarVisible) {
      setAvatarVisible(false)
    }
    else if(wearableVisible) {
      setWearableVisible(false)
    }
    else if(parcelVisible) {
      // Not recently modified
      if(!modified) {
        setMenuVisible(false)
        setParcelVisible(false)
      }
      // Modified without saving
      else {
        setConfirmVisible(true)
      }
      window.BITMAP_PARCELS = null
      window.BITMAP_EDITING = ''
    }
    else if(menuVisible) {
      setMenuVisible(false)
    }
  }
  const handleCancel = (e) => {
    setParcelVisible(false)
    setConfirmVisible(false)
    props.onRefreshUserParcel(parseInt(parcelNumValue));
  }
  const handleOpenGuide = async (e) => {
    handleClose()
    setGuideVisible(!guideVisible)
  }
  const handleOpenNames = async (e) => {
    handleClose()
    setLoading(true)
    setNameVisible(true)
    // Retrieve usernames
    const namesResult = await props.onGetUserNames()
    //console.log('namesResult',namesResult.data.names)
    if(namesResult.success)
      setNamesInventory(namesResult.data.names)
    setLoading(false)
  }
  const handleRefreshNames = async (e) => {
    setLoading(true)
    // Retrieve usernames
    const namesResult = await props.onGetUserNames(true)
    //console.log('namesResult',namesResult)
    if(namesResult.success)
      setNamesInventory(namesResult.data.names)
    setLoading(false)
  }
  const handleOpenAvatars = async (e) => {
    handleClose()
    setLoading(true)
    setAvatarVisible(true)
    // Retrieve avatars
    const avatarsResult = await props.onGetUserAvatars()
    //console.log('avatarsResult',avatarsResult)
    if(avatarsResult.success) {
      setUserAvatarValue(avatarsResult.data.user.avatar_ins)
      setAvatarsInventory(avatarsResult.data.avatars)
    }
    setLoading(false)
  }
  const handleRefreshAvatars = async (e) => {
    setLoading(true)
    // Retrieve avatars
    const avatarsResult = await props.onGetUserAvatars(true)
    //console.log('avatarsResult',avatarsResult)
    if(avatarsResult.success) {
      setUserAvatarValue(avatarsResult.data.user.avatar_ins)
      setAvatarsInventory(avatarsResult.data.avatars)
    }
    setLoading(false)
  }
  const handleOpenWearables = async (e) => {
    handleClose()
    setLoading(true)
    setWearableVisible(true)
    // Retrieve wearables
    const wearablesResult = await props.onGetUserWearables()
    //console.log('wearablesResult',wearablesResult)
    if(wearablesResult.success) {
      setUserWearablesValue(wearablesResult.data.user.wearables_ins)
      setWearablesInventory(wearablesResult.data.wearables)
    }
    setLoading(false)
  }
  const handleRefreshWearables = async (e) => {
    setLoading(true)
    // Retrieve wearables
    const wearablesResult = await props.onGetUserWearables(true)
    //console.log('wearablesResult',wearablesResult)
    if(wearablesResult.success) {
      setUserWearablesValue(wearablesResult.data.user.wearables_ins)
      setWearablesInventory(wearablesResult.data.wearables)
    }
    setLoading(false)
  }
  const handleOpenParcels = async (e) => {
    handleClose()
    setLoading(true)
    setParcelVisible(true)
    setModified(false)
    setParcelNumValue('')
    window.BITMAP_EDITING = ''
    // Retrieve parcels
    const parcelsResult = await props.onGetUserParcels()
    console.log('parcelsResult',parcelsResult)
    if(parcelsResult.success) {
      setParcelsInventory(parcelsResult.data.parcels)
      window.BITMAP_PARCELS = parcelsResult.data.parcels.reduce((parcelNums,parcel) => { parcelNums.push(parcel.details.parcel); return parcelNums; },[])
    }
    else
      window.BITMAP_PARCELS = []
    setLoading(false)
  }
  const handleRefreshParcels = async (e) => {
    setLoading(true)
    setParcelVisible(true)
    setModified(false)
    setParcelNumValue('')
    window.BITMAP_EDITING = ''
    // Retrieve parcels
    const parcelsResult = await props.onGetUserParcels(true)
    //console.log('parcelsResult',parcelsResult)
    if(parcelsResult.success)
      setParcelsInventory(parcelsResult.data.parcels)
    setLoading(false)
  }
  const handleLogout = (e) => {
    // Pop Up Confirm
    window.vm.$children[0].setConfirm({
      show: true,
      title: 'SIGN OUT',
      prompt: 'Are you sure you want to sign out?',
      cancel: 'Cancel',
      okay: 'Okay',
      red: false,
      positive: () => {
        console.log('Logout')
        // Clear local storage
        const keys = ['access_id','access_token','access_level','username','img_profile','img_type','wallet','btc_address','inscriptions','payment_address']
        for(let key in keys)
          localStorage[keys[key]] = '';
        // Refresh page
        location.reload();
      },
      negative: () => {
        console.log('Cancel Logout')
      }
    })
  }
  const handleSelectName = (nameIns) => {
    const nameFound = namesInventory.find((name) => {
      return name.inscription_id == nameIns
    })
    nameIns = nameFound.details.name == userNameValue ? '' : nameIns
    // Send request to update username
    props.onSaveUserName(nameIns);
    const nameFound2 = namesInventory.find((name) => {
      return name.inscription_id == nameIns
    })
    props.userData.username = nameFound2 ? nameFound2.details.name : props.userData.btc_address.slice(-6)
    setUserNameValue(props.userData.username)
  }
  const handleSelectAvatar = (avatarIns) => {
    avatarIns = avatarIns == userAvatarValue ? '' : avatarIns
    // Send request to update selected avatar
    props.onSaveUserAvatar(avatarIns);
    setUserAvatarValue(avatarIns)
  }
  const handleSelectWearable = (wearableIns) => {
    // Send request to update selected avatar
    props.onSaveUserWearable(wearableIns);
    // Add or remove wearable
    const index = userWearablesValue ? userWearablesValue.indexOf(wearableIns) : -1
    let userWearables = userWearablesValue ? [...userWearablesValue] : []
    // Unselect
    if(index >= 0)
      userWearables.splice(index,1)
    // Select
    else if(!userWearables)
      userWearables = [wearableIns]
    else
      userWearables.push(wearableIns)
    //console.log('userWearables',userWearables)

    setUserWearablesValue(userWearables)
    //console.log('userWearablesValue',userWearablesValue)
  }
  const handleSelectParcel = (parcelNum) => {
    setParcelNumValue(parcelNum)
    window.BITMAP_EDITING = parcelNum
    // Send request for parcel's MML contents
    props.onReadUserParcel(parcelNum);
  }

  const handleTypeSwitch = (type: CustomAvatarType) => {
    setCustomAvatarType(type);
    setCustomAvatarValue("");
  };

  const getPlaceholderByType = (type: CustomAvatarType) => {
    switch (type) {
      case CustomAvatarType.meshFileUrl:
        return "https://.../avatar.glb";
      case CustomAvatarType.mmlUrl:
        return "https://.../avatar.html";
      case CustomAvatarType.mml:
        return '<m-character src="https://link-to-avatar">\n</m-character';
    }
  };

  return (
    <>
      <div className={styles.menuButton} onClick={handleRootClick}>
        {(!menuVisible && !nameVisible && !avatarVisible && !wearableVisible && !parcelVisible) && (
          <button className={styles.openTab} onClick={handleOpen}>
            <img src={`data:image/svg+xml;utf8,${encodeURIComponent(ProfileIcon)}`} />
          </button>
        )}
        {(menuVisible || nameVisible  || avatarVisible || wearableVisible || parcelVisible) && (
          <button className={styles.closeButton} onClick={handleClose}>
            X
          </button>
        )}
        {(!menuVisible && !nameVisible && !avatarVisible && !wearableVisible && !parcelVisible) && (
          <button hint="Guide" className={`hint hint-left ${styles.guideTab}`} onClick={handleOpenGuide}>
            <img src={`data:image/svg+xml;utf8,${encodeURIComponent(GuideIcon)}`} />
          </button>
        )}
        {(menuVisible || nameVisible  || avatarVisible || wearableVisible || parcelVisible) && (
          <div className={styles.tabs}>
            <div className={styles.tabsBox}>
              <div className={styles.nameTab}>
                <button hint="Usernames" className={`hint hint-left`} onClick={handleOpenNames}>
                  <img src={`data:image/svg+xml;utf8,${encodeURIComponent(ProfileIcon)}`} />
                </button>
              </div>
              <div className={styles.avatarTab}>
                <button hint="Avatars" className={`hint hint-left`} onClick={handleOpenAvatars}>
                  <img src={`data:image/svg+xml;utf8,${encodeURIComponent(AvatarIcon)}`} />
                </button>
              </div>
              <div className={styles.wearableTab}>
                <button hint="Wearables" className={`hint hint-left`} onClick={handleOpenWearables}>
                  <img src={`data:image/svg+xml;utf8,${encodeURIComponent(WearableIcon)}`} />
                </button>
              </div>
              <div className={styles.parcelTab}>
                <button hint="Parcels" className={`hint hint-left`} onClick={handleOpenParcels}>
                  <img src={`data:image/svg+xml;utf8,${encodeURIComponent(ParcelIcon)}`} />
                </button>
              </div>
              <div className={styles.logoutTab}>
                <button hint="Sign Out" className={`hint hint-left hint-nowrap`} onClick={handleLogout}>
                  <img src={`data:image/svg+xml;utf8,${encodeURIComponent(LogoutIcon)}`} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {guideVisible && (
        <div className={styles.guideBox}>
          <div className={styles.guideCol}>
            MOVE
            <div className={styles.guideRow}>
              <div className={styles.guideKey}>W</div>
            </div>
            <div className={styles.guideRow}>
              <div className={styles.guideKey}>A</div>
              <div className={styles.guideKey}>S</div>
              <div className={styles.guideKey}>D</div>
            </div>
          </div>
          <div className={styles.guideMouse}>
            LOOK
            <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 100 100">
             <path d="m48.332 25.691c-12.723 0.85547-22.777 11.445-22.777 24.387v20.789c0 13.5 10.941 24.445 24.441 24.445h0.007812c13.5 0 24.441-10.945 24.441-24.445v-20.789c0-13.004-10.152-23.633-22.965-24.398-0.32031-2.8125-1.582-5.4453-3.6016-7.4648-2.3555-2.3555-5.5586-3.6797-8.8906-3.6797h-2.0273c-4.5742 0-8.2812-3.7109-8.2812-8.2812v-0.003906c0-0.86328-0.70313-1.5625-1.5625-1.5625-0.86328 0-1.5625 0.69922-1.5625 1.5625v0.003906c0 6.3008 5.1055 11.406 11.406 11.406h2.0273c2.5039 0 4.9102 0.99219 6.6797 2.7656 1.4336 1.4336 2.3594 3.2852 2.6641 5.2656zm22.988 38.289v6.8867c0 11.777-9.543 21.32-21.316 21.32h-0.007812c-11.773 0-21.316-9.543-21.316-21.32v-6.8867zm-22.883-10.254c-0.92969-0.19141-1.7891-0.65234-2.4648-1.332-0.92188-0.91797-1.4375-2.168-1.4375-3.4648v-8.3281c0-1.3008 0.51562-2.5469 1.4375-3.4688 0.67578-0.67969 1.5352-1.1406 2.4648-1.332v-6.9844c-11.047 0.80078-19.758 10.012-19.758 21.262v10.777h19.758zm3.125 7.1289h19.758v-10.777c0-11.25-8.7109-20.461-19.758-21.262v6.9844c0.92969 0.19141 1.7891 0.65234 2.4648 1.332 0.92188 0.92188 1.4375 2.168 1.4375 3.4688v8.3281c0 1.2969-0.51562 2.5469-1.4375 3.4648-0.67578 0.67969-1.5352 1.1406-2.4648 1.332zm0.77734-20.254v8.3281c0 0.46875-0.1875 0.92187-0.51953 1.2539-0.33203 0.33594-0.78516 0.52344-1.2578 0.52344h-1.125c-0.47266 0-0.92578-0.1875-1.2578-0.52344-0.33203-0.33203-0.51953-0.78516-0.51953-1.2539v-8.3281c0-0.47266 0.1875-0.92578 0.51953-1.2578 0.33203-0.33594 0.78516-0.52344 1.2578-0.52344h1.125c0.47266 0 0.92578 0.1875 1.2578 0.52344 0.33203 0.33203 0.51953 0.78516 0.51953 1.2578z" fill-rule="evenodd"/></svg>
          </div>
        </div>
      )}
      {nameVisible && (
        <div className={styles.avatarSelectionContainer}>
          <h2>
            My Usernames
            <img src={`data:image/svg+xml;utf8,${encodeURIComponent(RefreshIcon)}`} onClick={handleRefreshNames} className={`${loading ? styles.rotate : ''}`} />
          </h2>

          <div className={styles.none}>
            {userNameValue}
          </div>
          {!namesInventory || !namesInventory.length && (
            <div className={styles.none}>
              No usernames found
              <br />
              {(window.INSCRIBE_PARCEL_TEXT)}
            </div>
          )}
          <div>
            {namesInventory && namesInventory.map((name) => {
              return (
                <div className={`${styles.inscription} ${name.details.name==userNameValue ? styles.inscriptionSel : ''}`} onClick={(e) => handleSelectName(name.inscription_id)}>
                  {name.details.name}
                </div>
              )
            })}
          </div>
        </div>
      )}
      {avatarVisible && props.allowCustomAvatars && (
        <div className={`${styles.avatarSelectionContainer}`}>
          <h2>
            My Avatars
            <img src={`data:image/svg+xml;utf8,${encodeURIComponent(RefreshIcon)}`} onClick={handleRefreshAvatars} className={`${loading ? styles.rotate : ''}`} />
          </h2>
          {!avatarsInventory || !Object.keys(avatarsInventory).length && (
            <div className={styles.none}>
              No avatars found
              <br />
              Mint on <a href="https://mscribe.io" target="_blank">Mscribe.io</a>
            </div>
          )}
          {props.userData.access_level >= 8 && (
          <div>
            <input
              type="radio"
              id="html"
              name="customAvatarType"
              onChange={() => handleTypeSwitch(CustomAvatarType.mmlUrl)}
              defaultChecked={customAvatarType === CustomAvatarType.mmlUrl}
              checked={customAvatarType === CustomAvatarType.mmlUrl}
            />
            <label htmlFor="html">MML URL</label>
            <input
              type="radio"
              id="mml"
              name="customAvatarType"
              onChange={() => handleTypeSwitch(CustomAvatarType.mml)}
              defaultChecked={customAvatarType === CustomAvatarType.mml}
              checked={customAvatarType === CustomAvatarType.mml}
            />
            <label htmlFor="mml">MML</label>
            <input
              type="radio"
              id="glb"
              name="customAvatarType"
              onChange={() => handleTypeSwitch(CustomAvatarType.meshFileUrl)}
              defaultChecked={customAvatarType === CustomAvatarType.meshFileUrl}
              checked={customAvatarType === CustomAvatarType.meshFileUrl}
            />
            <label htmlFor="glb">Mesh URL</label>
            {selectedAvatar?.isCustomAvatar && <SelectedPill />}
            <div className={styles.customAvatarInputSection}>
              {customAvatarType === CustomAvatarType.mml ? (
                <textarea
                  ref={textareaRef}
                  className={styles.customAvatarInput}
                  value={customAvatarValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyPress}
                  placeholder={getPlaceholderByType(customAvatarType)}
                  rows={4}
                />
              ) : (
                <input
                  ref={inputRef}
                  className={styles.customAvatarInput}
                  value={customAvatarValue}
                  onKeyDown={handleKeyPress}
                  onChange={handleInputChange}
                  placeholder={getPlaceholderByType(customAvatarType)}
                />
              )}
              <button disabled={!customAvatarValue} type="button" onClick={addCustomAvatar}>
                Set
              </button>
            </div>
          </div>
          )}
          <div>
            {avatarsInventory && Object.entries(avatarsInventory).map(([index, avatar]) => {
              if(avatar.art_inscription)
                return (
                  <div className={`${styles.inscription} ${avatar.inscription_id==userAvatarValue ? styles.inscriptionSel : ''}`}>
                    <iframe src={'https://mmlviewer-ac91716b4d21.herokuapp.com/examples?src=https://ordinals.com/content/'+avatar.art_inscription+'&MINT_INSCRIPTION_ID='+avatar.inscription_id}></iframe>
                    <div onClick={(e) => handleSelectAvatar(avatar.inscription_id)}></div>
                  </div>
                )
              else
                return (
                  <div className={`${styles.inscription} ${avatar.inscription_id==userAvatarValue ? styles.inscriptionSel : ''}`} onClick={(e) => handleSelectAvatar(avatar.inscription_id)}>
                    {avatar.inscription_id}
                  </div>
                )
            })}
          </div>
        </div>
      )}
      {wearableVisible && (
        <div className={styles.avatarSelectionContainer}>
          <h2>
            My Wearables
            <img src={`data:image/svg+xml;utf8,${encodeURIComponent(RefreshIcon)}`} onClick={handleRefreshWearables} className={`${loading ? styles.rotate : ''}`} />
          </h2>

          {!wearablesInventory || !Object.keys(wearablesInventory).length && (
            <div className={styles.none}>
              No wearables found
              <br />
              Mint on <a href="https://mscribe.io" target="_blank">Mscribe.io</a>
            </div>
          )}
          <div>
            {wearablesInventory && Object.entries(wearablesInventory).map(([index, wearable]) => {
              if(wearable.art_inscription)
                return (
                  <div className={`${styles.inscription} ${userWearablesValue && userWearablesValue.includes(wearable.inscription_id) ? styles.inscriptionSel : ''}`}>
                    <iframe src={'https://mmlviewer-ac91716b4d21.herokuapp.com/examples?src=https://ordinals.com/content/'+wearable.art_inscription+'&MINT_INSCRIPTION_ID='+wearable.inscription_id}></iframe>
                    <div onClick={(e) => handleSelectWearable(wearable.inscription_id)}></div>
                  </div>
                )
              else
                return (
                  <div className={`${styles.inscription} ${userWearablesValue && userWearablesValue.includes(wearable.inscription_id) ? styles.inscriptionSel : ''}`} onClick={(e) => handleSelectWearable(wearable.inscription_id)}>
                    {wearable.inscription_id}
                  </div>
                )
            })}
          </div>
        </div>
      )}
      {confirmVisible && (
        <div className={styles.confirmContainer}>
          <h2>Would you like to save your changes before closing?</h2>
          <div className={styles.confirmDesc}>All modifications will be lost if not saved.</div>
          <div className={styles.confirmButtons}>
            <button onClick={handleCancel}>Don't Save</button>
            <button onClick={handleParcelSave}>Save Changes</button>
          </div>
        </div>
      )}
      {!confirmVisible && parcelVisible && (
        <div className={`${styles.avatarSelectionContainer}`}>
          <div className={styles.parcelEditorUi}>

            {!parcelNumValue && (
            <div>
              <h2>
                My Parcels
                <img src={`data:image/svg+xml;utf8,${encodeURIComponent(RefreshIcon)}`} onClick={handleRefreshParcels} className={`${loading ? styles.rotate : ''}`} />
              </h2>
              {!parcelsInventory || !parcelsInventory.length && (
                <div className={styles.none}>
                  No parcels found
                  <br />
                  Mint on <a href="https://mscribe.io" target="_blank">Mscribe.io</a>
                </div>
              )}
              {parcelsInventory && parcelsInventory.map(parcel => {
                return (
                  <span className={`${styles.parcelBox} ${parcel.details.parcel==parcelNumValue ? styles.parcelSel : ''}`}>
                    <div className={styles.parcelThumbnail} onClick={(e) => handleSelectParcel(parcel.details.parcel)}>
                      <img src={'https://mscribe.io/api/blocks/image/'+parcel.details.parcel+'.'+parcel.block} />
                    </div>
                    <div className={styles.parcelFlex}>
                      <div className={styles.parcelDetails}>
                        <h3>{parcel.details.parcel}</h3>
                        <span>{parcel.details.size} x {parcel.details.size}</span>
                      </div>
                      <button className={'hint hint-right'} hint="Teleport" onClick={(e) => handleTeleportClick(parcel.details.parcel)}>
                        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 100 100">
                         <path d="m66.973 81.52c-0.30859 1.3477-0.65234 2.5938-1.0273 3.7148-1.1797 3.5273-2.6914 5.7734-4.6172 5.7734-1.3828 0-2.5508-1.1719-3.5391-3.1367l0.23047-0.83203c0.23828-0.85156 0.46484-1.8086 0.6875-2.8086 0.9375 3.0234 1.8867 4.582 2.625 4.582 0.93359 0 2.2148-2.4805 3.3711-7.3203 0.37891-1.582 0.74219-3.418 1.0781-5.5039 1.0195-6.3672 1.7656-15.047 1.7656-25.988s-0.75-19.625-1.7734-25.992c-0.33594-2.0859-0.69922-3.9219-1.0781-5.5039-1.1562-4.8398-2.4375-7.3203-3.3711-7.3203-0.73438 0-1.6875 1.5586-2.625 4.582-0.21875-0.99219-0.44531-1.9453-0.6875-2.8086l-0.23047-0.83203c0.98828-1.9648 2.1562-3.1367 3.5391-3.1367 1.9219 0 3.4336 2.2461 4.6172 5.7734 0.375 1.1211 0.71875 2.3672 1.0273 3.7148 2.1016 9.1094 2.7773 22.738 2.7773 31.523s-0.67578 22.414-2.7695 31.52zm-11.176-48.566c0.13281-1.5938 0.29297-3.1016 0.46875-4.5234 0-0.03125 0-0.074219 0.015625-0.10156 0.10156-0.90625 0.21875-1.7852 0.35156-2.6211 0.015626-0.16016 0.042969-0.32031 0.074219-0.48438l-0.36719-2.1055h-0.015625c-0.058594-0.37891-0.11719-0.74609-0.17578-1.1133l-0.36719-2.1641c-0.058594-0.32031-0.11719-0.62891-0.19141-0.9375-0.085938 0.39453-0.17578 0.78906-0.26172 1.1992-0.10156 0.54297-0.20312 1.0977-0.30859 1.6523-0.11719 0.66016-0.23438 1.3164-0.32031 2.0039-0.11719 0.67188-0.21875 1.375-0.30859 2.0938-0.14453 1.0547-0.26172 2.1367-0.39453 3.2344l-0.21875 2.1953c-0.14453 1.4766-0.26172 2.9727-0.35156 4.4492-0.058593 0.70312-0.10156 1.3906-0.13281 2.0625-0.10156 1.5078-0.17578 3-0.21875 4.4492-0.015625 0.35156-0.027344 0.70312-0.042969 1.0547-0.03125 0.6875-0.042969 1.3594-0.058594 2.0352-0.015625 0.52734-0.027344 1.0391-0.027344 1.5508-0.015624 0.48438-0.015624 0.96484-0.015624 1.4336-0.015626 0.58594-0.015626 1.1406-0.015626 1.6836s0 1.0977 0.015626 1.6836c0 0.46875 0 0.95312 0.015624 1.4336v0.33594c0.015626 0.39453 0.015626 0.80469 0.027344 1.2148 0.015625 0.67188 0.03125 1.3477 0.058594 2.0352 0.015625 0.35156 0.027344 0.70312 0.042969 1.0547 0.042969 1.4492 0.11719 2.9414 0.21875 4.4492 0.03125 0.6875 0.074219 1.375 0.13281 2.0625 0.085938 1.4766 0.20312 2.9688 0.35156 4.4492l0.21875 2.1953c0.13281 1.0977 0.25 2.1797 0.39453 3.2344 0.085937 0.71875 0.19141 1.418 0.30859 2.0938 0.085937 0.6875 0.20312 1.3477 0.32031 2.0039 0.10156 0.55469 0.20312 1.1133 0.30859 1.6523 0.085937 0.41016 0.17578 0.80469 0.26172 1.1992 0.058594-0.30859 0.11719-0.60156 0.17578-0.92188l0.015625-0.11719 0.36719-2.0781 0.042968-0.25c0.042969-0.27734 0.085938-0.55469 0.13281-0.84766l0.37891-2.1211c-0.015625-0.03125-0.015625-0.042969-0.015625-0.074219-0.03125-0.13281-0.042969-0.26172-0.058594-0.39453-0.13281-0.83594-0.25-1.7109-0.35156-2.6211-0.015625-0.03125-0.015625-0.074219-0.015625-0.10156-0.17578-1.418-0.33594-2.9258-0.46875-4.5234-0.074219-0.90625-0.14453-1.8281-0.21875-2.7812-0.28906-4.2109-0.46484-8.9844-0.46484-14.266s0.17578-10.055 0.46875-14.27c0.070313-0.94922 0.14453-1.8711 0.21875-2.7773zm13.969-22.098c-1.3164 0-2.4297 1.1016-3.3711 2.9492l0.24219 0.73047c0.26172 0.78125 0.50391 1.6562 0.74219 2.5703 0.85547-2.6797 1.7148-4.0547 2.3867-4.0547 2.0508 0 5.8789 12.691 5.8789 36.953 0 24.262-3.8281 36.953-5.8789 36.953-0.67188 0-1.5312-1.375-2.3867-4.0547-0.23438 0.91406-0.48047 1.7891-0.74219 2.5703l-0.24609 0.73047c0.94141 1.8477 2.0547 2.9492 3.3711 2.9492 6.3867 0 8.0742-25.605 8.0742-39.145 0.003906-13.547-1.6836-39.152-8.0703-39.152zm-27.609 17.332c3.3672 0.45312 6.4648-1.9141 6.9141-5.2812 0.45313-3.3672-1.9141-6.4648-5.2812-6.9141-3.3672-0.45313-6.4648 1.9141-6.9141 5.2812-0.44922 3.3672 1.9141 6.4609 5.2812 6.9141zm5.6016 7.7148s-0.44141 2.7227-1.0117 6.2305l5.0703 4.8633v-7.3555l-4.0508-3.8867c-0.007813 0.050782 0 0.09375-0.007813 0.14844zm-13.84 24.676c-0.21484-0.22266-0.41016-0.48047-0.61719-0.80859-0.21875-0.34766-0.40234-0.71875-0.54297-1.1133-0.14453-0.40625-0.24609-0.83594-0.30469-1.2773-0.003906-0.039062-0.003906-0.078125-0.007813-0.11719l-0.042968 0.11719-1.1445 3.2812-6.6172 18.992c-0.60156 1.7305 0.3125 3.6211 2.043 4.2227 0.35938 0.12891 0.73047 0.1875 1.0898 0.1875 1.3711 0 2.6562-0.85938 3.1328-2.2266l5.1562-14.805 1.4648-4.2109-2.7734-1.6055c-0.25391-0.15234-0.55859-0.34766-0.83594-0.63672zm27.043-10.578c0-10.254-0.78516-23.336-2.6836-32.75-0.29297-1.4609-0.61719-2.832-0.96484-4.0938-1.3711-4.9453-3.1797-8.1562-5.4961-8.1562-2.707 0-4.7148 4.3789-6.1523 10.785 0.73438 0.3125 1.4062 0.73438 1.9766 1.2656 1.3906-6.5156 3.0195-9.8516 4.1758-9.8516 1.1602 0 2.8008 3.3711 4.1953 9.9492 0.36719 1.7344 0.71875 3.6875 1.0391 5.8633 1.0039 6.8438 1.7148 15.859 1.7148 26.988s-0.71094 20.145-1.7188 26.988c-0.32031 2.1758-0.67188 4.1328-1.0391 5.8633-1.3945 6.582-3.0352 9.9531-4.1953 9.9531-1.0703 0-2.5547-2.875-3.875-8.5039-1.2773-0.69922-2.1328-2.082-2.1328-3.6445v-12.945l-0.28125-0.16406-2.2617-1.3086c1.0781 14.652 3.6953 28.762 8.5547 28.762 2.3164 0 4.125-3.2109 5.4961-8.1562 0.34766-1.2578 0.67188-2.6328 0.96484-4.0938 1.9023-9.4141 2.6836-22.496 2.6836-32.75zm-15.043-21.719c-0.61328 0.29297-1.2812 0.48047-1.9727 0.57812 0.63281 0.26953 1.2461 0.64453 1.8047 1.1328 0.054688-0.57813 0.10938-1.1562 0.16797-1.7109zm-18.727 21.699 4.3008-8.668 3.4102-2.4805-1.6719 15.676-0.16016 1.5c-0.03125 0.42969-0.027343 0.86328 0.027344 1.2891 0.050782 0.38672 0.14062 0.76953 0.26953 1.1406 0.125 0.34375 0.28516 0.67578 0.48047 0.98438 0.16016 0.25 0.33203 0.49219 0.53906 0.70703 0.20703 0.21875 0.44141 0.375 0.69922 0.52344 0.22656 0.13281 1.1094 0.64453 1.3203 0.76953 0.066406 0.039063 0.13672 0.078125 0.20312 0.11719l9.9258 5.7461v13.367c0 1.582 1.0742 3.0234 2.625 3.3359 0.99609 0.20312 1.9297-0.054687 2.6484-0.58594l0.007812-20.664c-0.10156-0.074219-0.1875-0.16406-0.29688-0.22656l-8.0312-4.6602c0.039063-0.13281 3.6094-22.031 3.6094-22.031 0.42578-3.3594-1.3945-5.5742-3.7422-6.4219-1.5117-0.55859-3.25-0.51953-4.7266 0.17187l-1.6602 1.207-9.1172 6.6211c-0.35937 0.25391-0.63672 0.58203-0.82422 0.96875l-4.5898 9.2578c-0.60938 1.2266-0.20703 2.7578 0.97656 3.4453 0.44922 0.26172 0.92578 0.39453 1.3984 0.39453 0.98438 0.003906 1.9141-0.54297 2.3789-1.4844z"/></svg>
                      </button>
                    </div>
                  </span>
                )
              })}
            </div>
            )}

            {parcelNumValue && (
            <div>
              <h2>Parcel Editor
                {props.userData.access_level >= 9 && (<div className={styles.parcelInput}>
                  <div>Parcel Number</div>
                  <input
                    ref={parcelnumRef}
                    type="number"
                    value={parcelNumValue}
                    placeholder="parcel number"
                    onKeyDown={handleKeyPress}
                    onChange={handleParcelNumChange}
                  />
                </div>)}
              </h2>
              <div className={styles.parcelFlex}>
                <div className={styles.parcelInfo}>
                  <div>{parcelNumValue}</div>
                  {parcelValue() && (<span>{parcelValue()} x {parcelValue()}</span>)}
                </div>
                <div className={styles.parcelThumbnail}>
                  <img src={'https://mscribe.io/api/blocks/image/'+parcelNumValue+'.'+window.BITMAP_DATA.height} />
                </div>
              </div>
              <textarea
                ref={parcelmmlRef}
                className={`${styles.parcelTextInput}`}
                value={props.parcelData}
                onChange={handleParcelMMLChange}
                onPaste={handleParcelMMLChange}
                onCut={handleParcelMMLChange}
                onKeyDown={handleParcelMMLInput}
                onFocus={handleParcelMMLFocus}
                placeholder="<m-cube></m-cube>"
                rows={4}
              />
              <div className={styles.parcelButtons}>
                <button onClick={handleParcelSave}>
                  Save Changes
                </button>
              </div>
            </div>
            )}
          </div>
        </div>
      )}
      {!!props.availableAvatars.length && (
        <div className={styles.avatarSelectionUi}>
          <div className={styles.avatarSelectionUiHeader}>
            <h2>Select your avatar</h2>
          </div>
          <div className={styles.avatarSelectionUiContent}>
            {props.availableAvatars.map((avatar, index) => {
              const isSelected =
                !selectedAvatar?.isCustomAvatar &&
                ((selectedAvatar?.meshFileUrl &&
                  selectedAvatar?.meshFileUrl === avatar.meshFileUrl) ||
                  (selectedAvatar?.mmlCharacterUrl &&
                    selectedAvatar?.mmlCharacterUrl === avatar.mmlCharacterUrl) ||
                  (selectedAvatar?.mmlCharacterString &&
                    selectedAvatar?.mmlCharacterString === avatar.mmlCharacterString));

              return (
                <div
                  key={index}
                  className={styles.avatarSelectionUiAvatar}
                  onClick={() => selectAvatar(avatar)}
                >
                  <div className={styles.avatarSelectionUiAvatarImgContainer}>
                    {isSelected && <SelectedPill />}
                    {avatar.thumbnailUrl ? (
                      <img src={avatar.thumbnailUrl} alt={avatar.name} />
                    ) : (
                      <div>No Image Available</div>
                    )}
                    <p>{avatar.name}</p>
                    <span className={styles.tooltipText}>{avatar.name}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </>
  );
};
